import React, { useEffect, useState } from 'react';
import { Loader2, ChevronDown, Target, BookOpen } from 'lucide-react';
import MotoShell from '../components/MotoShell';
import { api } from '../lib/api';

const COMP_COLORS = {
  C1: 'text-sky-300 border-sky-500/30',
  C2: 'text-violet-300 border-violet-500/30',
  C3: 'text-amber-300 border-amber-500/30',
  C4: 'text-emerald-300 border-emerald-500/30',
};

function SousCompetence({ sc }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02]" data-testid={`livret-sc-${sc.code}`}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-3 p-3 text-left">
        <span className="font-mono-spec text-sky-300 font-bold text-[12px] shrink-0 w-11">{sc.code}</span>
        <span className="text-white text-[13px] leading-snug flex-1">{sc.intitule}</span>
        <ChevronDown size={15} className={`text-white/40 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-3 pb-3.5 pl-[62px] space-y-3">
          {sc.objectif && (
            <div>
              <div className="text-white/45 text-[10px] font-mono-spec uppercase tracking-widest font-bold mb-1">Objectif</div>
              <p className="text-white/70 text-[12.5px] leading-relaxed">{sc.objectif}</p>
            </div>
          )}
          {(sc.criteres || []).length > 0 && (
            <div>
              <div className="text-white/45 text-[10px] font-mono-spec uppercase tracking-widest font-bold inline-flex items-center gap-1.5 mb-1.5"><Target size={11} /> Critères de validation</div>
              <ul className="space-y-1">
                {sc.criteres.map((c, i) => <li key={i} className="text-[12px] text-white/65 leading-snug flex gap-2"><span className="text-sky-400 shrink-0">▸</span>{c}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MotoLivretPage() {
  const [data, setData] = useState(null);

  useEffect(() => {
    document.title = 'Livret d\u2019apprentissage moto — 38 compétences REMC | Code 2 Roues';
    api.motoLivret().then(setData).catch(() => setData({ COMPETENCES_LIVRET: [] }));
  }, []);

  const comps = data?.COMPETENCES_LIVRET || [];
  const meta = data?.META_LIVRET;

  return (
    <MotoShell>
      <section className="max-w-4xl mx-auto px-4 lg:px-6 pt-10 sm:pt-14" data-testid="moto-livret-page">
        <div className="text-[10.5px] uppercase tracking-[0.22em] text-sky-400 font-bold font-mono-spec mb-2">Pédagogique · REMC officiel</div>
        <h1 className="font-serif-editorial text-[30px] sm:text-5xl leading-[1.05]">Le livret d&apos;apprentissage moto.</h1>
        <p className="text-white/60 text-[13.5px] sm:text-[15px] mt-3 max-w-2xl leading-relaxed">
          {meta?.description || 'Les 4 compétences et 38 sous-compétences officielles du permis A, avec objectifs et critères de validation.'}
          {meta?.source && <span className="block text-white/35 text-[11.5px] font-mono-spec mt-2">{meta.source}</span>}
        </p>
        {!data ? (
          <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-sky-400" size={28} /></div>
        ) : (
          <div className="mt-8 space-y-8 mb-6">
            {comps.map((c) => (
              <div key={c.code} data-testid={`livret-comp-${c.code}`}>
                <div className={`rounded-xl border bg-[#0b0f1a] p-4 sm:p-5 ${COMP_COLORS[c.code] || 'border-white/10'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`font-serif-editorial text-[30px] leading-none ${(COMP_COLORS[c.code] || '').split(' ')[0]}`}>{c.code}</span>
                    <div>
                      <div className="text-white font-semibold text-[14.5px] leading-snug">{c.titre}</div>
                      <div className="text-white/45 text-[11.5px] mt-0.5 inline-flex items-center gap-1.5"><BookOpen size={11} /> {c.sousCompetences?.length || 0} sous-compétences · {c.contexte}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-2.5 space-y-1.5">
                  {(c.sousCompetences || []).map((sc) => <SousCompetence key={sc.code} sc={sc} />)}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </MotoShell>
  );
}
